import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { toaster } from "baseui/toast";
import {
  useGetBuilding,
  usePostBuilding,
  usePatchBuildingId,
  useDeleteBuildingId,
} from "../api/generated";
import type { Building } from "../api/generated";

export const BUILDINGS_QUERY_KEY = ["/building"];

export const useBuildings = (params?: { limit?: number; search?: string }) => {
  const queryClient = useQueryClient();

  const { data: buildingsResponse, isLoading, error } = useGetBuilding({
    limit: params?.limit || 200,
    ...(params?.search ? { name_ilike: params.search } : {}),
  } as any);

  // useGetBuilding doesn't pass order_by, so sort client-side. localeCompare with
  // numeric+base sensitivity handles accents, mixed case, and leading numbers correctly.
  const allBuildings: Building[] = ((buildingsResponse?.data as any)?.data || buildingsResponse?.data || [])
    .slice()
    .sort((a: Building, b: Building) =>
      (a.name ?? '').localeCompare(b.name ?? '', undefined, { numeric: true, sensitivity: 'base' })
    );

  const createMutation = usePostBuilding({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: BUILDINGS_QUERY_KEY });
        toaster.positive("Building created successfully", { autoHideDuration: 3000 });
      },
      onError: () => {
        toaster.negative("Error creating building", { autoHideDuration: 3000 });
      },
    },
  });

  const patchMutation = usePatchBuildingId({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: BUILDINGS_QUERY_KEY });
        toaster.positive("Building updated successfully", { autoHideDuration: 3000 });
      },
      onError: () => {
        toaster.negative("Error updating building", { autoHideDuration: 3000 });
      },
    },
  });

  const deleteMutation = useDeleteBuildingId({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: BUILDINGS_QUERY_KEY });
        toaster.positive("Building deleted successfully", { autoHideDuration: 3000 });
      },
      onError: () => {
        toaster.negative("Error deleting building", { autoHideDuration: 3000 });
      },
    },
  });

  return {
    allBuildings,
    isLoading,
    error,
    createBuilding: (data: Building) => createMutation.mutateAsync({ data }),
    patchBuilding: (id: string, data: Partial<Building>) =>
      patchMutation.mutateAsync({ id, data }),
    deleteBuilding: (id: string) => deleteMutation.mutateAsync({ id }),
    isCreating: createMutation.isPending,
    isUpdating: patchMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};

export const useBuildingEstatesPreview = (buildingName: string, enabled: boolean) => {
  return useQuery({
    queryKey: ["building-estates-preview", buildingName],
    queryFn: async () => {
      if (!buildingName.trim()) return [];
      const token = localStorage.getItem("token");
      const businessData = sessionStorage.getItem("businessData");
      let apiKey = "";
      if (businessData) {
        try {
          apiKey = JSON.parse(businessData).api_key;
        } catch {}
      }
      const params = new URLSearchParams({
        name_ilike: buildingName.trim(),
        limit: "200",
        expand: "estate_group",
        order_by: "name",
        order_direction: "ASC",
      });
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/crud/estate?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Api-Key": apiKey,
          },
        }
      );
      if (!response.ok) return [];
      const json = await response.json();
      return (json?.data || []) as Array<{ id: number; name: string; building_id?: number | null }>;
    },
    enabled: enabled && buildingName.trim().length >= 2,
    staleTime: 10_000,
  });
};

export const useBulkAssignBuilding = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      estateIds,
      buildingId,
    }: {
      estateIds: number[];
      buildingId: number | null;
    }) => {
      const token = localStorage.getItem("token");
      const businessData = sessionStorage.getItem("businessData");
      let apiKey = "";
      if (businessData) {
        try {
          apiKey = JSON.parse(businessData).api_key;
        } catch {}
      }

      const results = await Promise.allSettled(
        estateIds.map((id) =>
          fetch(`${import.meta.env.VITE_API_BASE_URL}/crud/estate/${id}`, {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${token}`,
              "X-Api-Key": apiKey,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ building_id: buildingId }),
          })
        )
      );

      const failed = results.filter((r) => r.status === "rejected").length;
      return { total: estateIds.length, failed };
    },
    onSuccess: ({ total, failed }) => {
      queryClient.invalidateQueries({ queryKey: ["estates-list-paginated"] });
      queryClient.invalidateQueries({ queryKey: BUILDINGS_QUERY_KEY });
      if (failed > 0) {
        toaster.warning(`${total - failed}/${total} estates assigned. ${failed} failed.`, {
          autoHideDuration: 4000,
        });
      } else {
        toaster.positive(`${total} estates assigned to building successfully`, {
          autoHideDuration: 3000,
        });
      }
    },
    onError: () => {
      toaster.negative("Error assigning estates to building", { autoHideDuration: 3000 });
    },
  });
};
